import { type FormEvent, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError } from "../api/client";
import { profileChatApi, type ChatMessage, type Revision } from "../api/profileChat";
import DiffView from "./DiffView";
import { Alert, Button, Card, Spinner, Textarea } from "./ui";

const CHAT_KEY = ["profile", "chat"];
const PROFILE_KEY = ["profile"];

/**
 * Build your profile by talking to Claude.
 *
 * Claude interviews you and, when it has something concrete, proposes a
 * rewritten profile. Nothing is saved until you accept it — and you can edit
 * the proposal before you do.
 */
export default function ProfileChat() {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState("");
  const endRef = useRef<HTMLDivElement>(null);

  const { data, isPending } = useQuery({ queryKey: CHAT_KEY, queryFn: profileChatApi.read });

  const send = useMutation({
    mutationFn: (content: string) => profileChatApi.send(content),
    onSuccess: () => {
      setDraft("");
      queryClient.invalidateQueries({ queryKey: CHAT_KEY });
    },
  });

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [data?.messages.length, send.isPending]);

  if (isPending || !data) return <Spinner label="Loading conversation…" />;

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (draft.trim()) send.mutate(draft.trim());
  }

  const error = send.error instanceof ApiError ? send.error.message : null;

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card title="Conversation">
        <div className="flex h-[30rem] flex-col">
          <div className="flex-1 space-y-3 overflow-y-auto pe-1">
            {data.messages.length === 0 && <Opener />}
            {data.messages.map((message) => (
              <Bubble key={message.id} message={message} />
            ))}
            {send.isPending && (
              <div className="text-sm text-gray-400">Claude is thinking…</div>
            )}
            <div ref={endRef} />
          </div>

          <form onSubmit={onSubmit} className="mt-4 space-y-2">
            {error && <Alert kind="error">{error}</Alert>}
            <Textarea
              rows={3}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) onSubmit(e);
              }}
              placeholder="Tell Claude about your work…"
              className="font-sans"
            />
            <div className="flex items-center gap-3">
              <Button type="submit" disabled={!draft.trim() || send.isPending}>
                Send
              </Button>
              <span className="text-xs text-gray-400">⌘/Ctrl + Enter</span>
            </div>
          </form>
        </div>
      </Card>

      {data.revision ? (
        <ProposalReview revision={data.revision} />
      ) : (
        <Card title="Proposed changes">
          <p className="text-sm text-gray-500">
            When Claude has something concrete to record, its proposed rewrite appears here
            for you to accept, edit or reject. Nothing is saved to your profile until you
            accept it.
          </p>
        </Card>
      )}
    </div>
  );
}

function Opener() {
  return (
    <div className="rounded-md bg-gray-50 p-4 text-sm text-gray-600">
      <p className="font-medium text-gray-800">Start anywhere.</p>
      <p className="mt-1">
        Describe your most recent role, or paste a rough list of what you have done. Claude
        will ask follow-up questions and turn it into a profile.
      </p>
    </div>
  );
}

function Bubble({ message }: { message: ChatMessage }) {
  const mine = message.role === "user";
  return (
    <div className={mine ? "flex justify-end" : "flex justify-start"}>
      <div
        className={
          "max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap " +
          (mine ? "bg-accent-600 text-white" : "bg-gray-100 text-gray-800")
        }
      >
        {message.content}
        {message.revision_id && (
          <p className={"mt-1 text-xs " + (mine ? "text-white/70" : "text-gray-500")}>
            Proposed a change →
          </p>
        )}
      </div>
    </div>
  );
}

function ProposalReview({ revision }: { revision: Revision }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [body, setBody] = useState(revision.proposed_md);

  useEffect(() => {
    setBody(revision.proposed_md);
    setEditing(false);
  }, [revision.id, revision.proposed_md]);

  const settle = () => {
    queryClient.invalidateQueries({ queryKey: CHAT_KEY });
    queryClient.invalidateQueries({ queryKey: PROFILE_KEY });
  };

  const accept = useMutation({
    mutationFn: () => profileChatApi.accept(revision.id, editing ? body : undefined),
    onSuccess: settle,
  });
  const reject = useMutation({
    mutationFn: () => profileChatApi.reject(revision.id),
    onSuccess: settle,
  });

  const busy = accept.isPending || reject.isPending;

  return (
    <Card title="Proposed changes">
      <div className="space-y-4">
        {revision.rationale && <p className="text-sm text-gray-600">{revision.rationale}</p>}

        {editing ? (
          <Textarea rows={18} value={body} onChange={(e) => setBody(e.target.value)} />
        ) : (
          <DiffView diff={revision.diff} />
        )}

        <Alert kind="error">
          Accepting this marks every generated resume and cover letter stale, so you can
          regenerate them against the new profile.
        </Alert>

        <div className="flex flex-wrap items-center gap-3">
          <Button onClick={() => accept.mutate()} disabled={busy}>
            {accept.isPending ? "Applying…" : editing ? "Accept my edits" : "Accept"}
          </Button>
          <Button variant="secondary" onClick={() => setEditing(!editing)} disabled={busy}>
            {editing ? "Show diff" : "Edit first"}
          </Button>
          <button
            type="button"
            onClick={() => reject.mutate()}
            disabled={busy}
            className="text-sm text-gray-600 hover:text-gray-900 hover:underline"
          >
            Reject
          </button>
        </div>
      </div>
    </Card>
  );
}
